import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FaSearch } from 'react-icons/fa';
import { client } from '@php-beam/packages/frontend/src/api/client';

interface SearchResult {
  id: number;
  business_name: string;
  dba_name: string | null;
  state: string | null;
  status: string | null;
  url: string;
}

interface LeadSearchProps {
  onSelect: (lead: SearchResult) => void;
}

export default function LeadSearch({ onSelect }: LeadSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  const search = useCallback((q: string) => {
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    client
      .get<{ leads: SearchResult[] }>('/devtools/resources/leads/search', {
        params: { q },
      })
      .then((res) => {
        setResults(res.data.leads);
        setOpen(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <Box ref={containerRef} position="relative">
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          {loading ? <Spinner size="xs" /> : <FaSearch color="gray" />}
        </InputLeftElement>
        <Input
          placeholder="Search by name, ID, or DBA..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
        />
      </InputGroup>

      {open && results.length > 0 && (
        <List
          position="absolute"
          zIndex={10}
          bg="white"
          borderWidth={1}
          borderRadius="md"
          shadow="lg"
          mt={1}
          w="full"
          maxH="250px"
          overflowY="auto"
        >
          {results.map((lead) => (
            <ListItem
              key={lead.id}
              px={3}
              py={2}
              cursor="pointer"
              _hover={{ bg: 'purple.50' }}
              onClick={() => {
                onSelect(lead);
                setQuery('');
                setOpen(false);
              }}
            >
              <HStack justify="space-between">
                <Box>
                  <Text fontSize="sm" fontWeight="medium">
                    {lead.business_name}
                    {lead.dba_name && (
                      <Text as="span" color="gray.500">
                        {' '}
                        ({lead.dba_name})
                      </Text>
                    )}
                  </Text>
                  <Text fontSize="xs" color="gray.400">
                    ID: {lead.id}
                    {lead.state && ` · ${lead.state}`}
                  </Text>
                </Box>
                {lead.status && (
                  <Badge fontSize="xs" colorScheme="blue">
                    {lead.status}
                  </Badge>
                )}
              </HStack>
            </ListItem>
          ))}
        </List>
      )}

      {open && query.length >= 2 && results.length === 0 && !loading && (
        <Box
          position="absolute"
          zIndex={10}
          bg="white"
          borderWidth={1}
          borderRadius="md"
          shadow="lg"
          mt={1}
          w="full"
          p={3}
        >
          <Text fontSize="sm" color="gray.500">
            No leads found
          </Text>
        </Box>
      )}
    </Box>
  );
}
